/*
 * MMG-4335: the Save & Log decision for a repair-type asset, as a pure function so the pathway
 * matrix from the ticket can be tested without a browser. The three inputs are the engineer's answer
 * to the compliance validation question, whether every carried deficiency has been ticked, and
 * whether any measurement point on the asset is still failing.
 *
 * The mapping is the ticket's master flow (MMG-4335_a05.png), not an interpretation of it:
 *   YES + all ticked + no failures ....... save, asset passes                       (pathway 1)
 *   YES + some unticked .................. unaddressed-deficiencies modal           (pathway 1A)
 *   YES + all ticked + failures .......... recorded-failures modal, cannot pass     (pathway 1B)
 *   NO  + anything except "clean" ........ save, asset fails                        (pathways 2/2A/2B)
 *   NO  + all ticked + no failures ....... no-recorded-failures modal               (pathway 2C)
 *   unanswered ........................... Save & Log stays disabled
 */

import { findInUseGate, isInUseGateActive, resolveGateSelectedOptionID } from '../../actions/measurementPointInUseGateUtil';
import { ImeasurementPoint, ImeasurementPointAnswer } from '../../models';
import { measurementPointPassFailTypes } from '../../models-enums';

export type ComplianceValidationAnswer = 'yes' | 'no' | 'unanswered';

/**
 * The compliance question on THIS asset's question list, or undefined when the list has none.
 * The points map is global (every list the app has synced), so the search must be scoped to the
 * selected list's tabs - with the coverage migration flagging a question on every repair-flow
 * list, an unscoped find() returns some other list's question and the gate blocks on a question
 * the engineer cannot see.
 */
export const findComplianceValidationQuestion = (
    measurementPointsByID: { [id: string]: ImeasurementPoint },
    listTabIDs: Set<string>
): ImeasurementPoint | undefined =>
    Object.values(measurementPointsByID).find(
        point =>
            point.isComplianceValidation === true &&
            !point.isDeleted &&
            listTabIDs.has(point.measurementPointTabID)
    );

/**
 * Reads the engineer's answer through the option's IsCompliantOption flag. Never through the wording:
 * option text is translated for display and the "no" phrasing varies between questions.
 */
export const readComplianceValidationAnswer = (
    question: ImeasurementPoint,
    answers: ImeasurementPointAnswer[]
): ComplianceValidationAnswer => {
    const answer = answers.find(candidate => candidate.measurementPointID === question.id);
    const optionID = answer && answer.measurementPointSelectOptionID;

    if (!optionID) {
        return 'unanswered';
    }

    const option = (question.selectOptions || []).find(candidate => candidate.id === optionID);

    if (!option) {
        return 'unanswered';
    }

    return option.isCompliantOption === true ? 'yes' : 'no';
};

/**
 * True when the asset is "Not in Use", which hides every non-gate control on the question's tab -
 * including the compliance question itself. The gate MUST be skipped in that state: the engineer has
 * no way to answer a hidden question, so blocking Save & Log on it would strand them. A not-in-use
 * asset has nothing to validate anyway.
 */
export const isComplianceQuestionGatedOff = (
    question: ImeasurementPoint,
    measurementPointsByID: { [id: string]: ImeasurementPoint },
    answers: ImeasurementPointAnswer[],
    gateSelections: { [gateID: string]: string | null }
): boolean => {
    const pointsOnSameTab = Object.values(measurementPointsByID).filter(
        point => point.measurementPointTabID === question.measurementPointTabID && !point.isDeleted
    );
    const gate = findInUseGate(pointsOnSameTab);

    if (!gate) {
        return false;
    }

    return !isInUseGateActive(gate, resolveGateSelectedOptionID(gate, answers, gateSelections));
};

/** Measurement points the engineer left failing, used for the pathway 1B warning. */
export const findFailingMeasurementPoints = (
    measurementPointsByID: { [id: string]: ImeasurementPoint },
    answers: ImeasurementPointAnswer[]
): ImeasurementPoint[] =>
    answers
        .filter(answer => answer.pass === measurementPointPassFailTypes.fail)
        .map(answer => measurementPointsByID[answer.measurementPointID])
        .filter((point): point is ImeasurementPoint => point !== undefined);

export type DeficiencyGateOutcome =
    | { kind: 'blocked' }
    | { kind: 'save'; compliance: 'pass' | 'fail' }
    | { kind: 'modal'; modal: 'unaddressedDeficiencies' | 'recordedFailures' | 'noRecordedFailures' };

export interface DeficiencyGateInput {
    answer: ComplianceValidationAnswer;
    hasUnverifiedDeficiencies: boolean;
    hasFailingMeasurementPoints: boolean;
}

export const resolveDeficiencyGate = ({
    answer,
    hasUnverifiedDeficiencies,
    hasFailingMeasurementPoints
}: DeficiencyGateInput): DeficiencyGateOutcome => {
    if (answer === 'unanswered') {
        return { kind: 'blocked' };
    }

    if (answer === 'yes') {
        // an unticked deficiency contradicts "compliant" before any measurement point is considered
        if (hasUnverifiedDeficiencies) {
            return { kind: 'modal', modal: 'unaddressedDeficiencies' };
        }

        if (hasFailingMeasurementPoints) {
            return { kind: 'modal', modal: 'recordedFailures' };
        }

        return { kind: 'save', compliance: 'pass' };
    }

    // answered NO with nothing recorded against the asset - confirm before failing it
    if (!hasUnverifiedDeficiencies && !hasFailingMeasurementPoints) {
        return { kind: 'modal', modal: 'noRecordedFailures' };
    }

    return { kind: 'save', compliance: 'fail' };
};

/** Pathway 1A "Yes" and 2C "No" both fall through to failing the asset once the user confirms. */
export const outcomeAfterModalConfirm = (
    modal: 'unaddressedDeficiencies' | 'recordedFailures' | 'noRecordedFailures'
): DeficiencyGateOutcome =>
    modal === 'recordedFailures' ? { kind: 'blocked' } : { kind: 'save', compliance: 'fail' };
